/**
 * Theming.
 *
 *   - A BOARD has a visualTheme (preset id) and optionally an
 *     accentHue (0-359) that overrides the preset's accent colour.
 *   - A USER has an optional themePreference (preset id). When set,
 *     it overrides the board's visualTheme for this user only. NULL =
 *     "follow whatever the board picked."
 *
 * Resolution order (highest priority first):
 *   1. user.themePreference if non-null
 *   2. board.visualTheme if non-null
 *   3. THEMES[0] (ember)
 *
 * Then if (board.accentHue || user.themePreference == null) and a
 * board accent is set, we replace --color-clove* with that hue.
 */

export interface ThemePreset {
  id: string;
  label: string;
  description: string;
  /** Whether this is a light or dark base palette. */
  surface: 'light' | 'dark';
  vars: Record<string, string>;
}

const DEFAULT_THEME = {
  '--color-ink': '#0d0d12',
  '--color-ink-2': '#15151d',
  '--color-ink-3': '#1c1c27',
  '--color-ink-4': '#252533',
  '--color-ink-rim': '#2c2c3a',
  '--color-cream': '#f5f1ea',
  '--color-cream-2': '#cfc9bd',
  '--color-cream-3': '#8b857b',
  '--color-cream-4': '#565158',
  '--color-clove': '#ff6a4d',
  '--color-clove-2': '#ff9a82',
  '--color-clove-3': '#4a1d18',
};

export const THEMES: ThemePreset[] = [
  {
    id: 'ember',
    label: 'Ember',
    description: 'Warm dark. Default.',
    surface: 'dark',
    vars: DEFAULT_THEME,
  },
  {
    id: 'noir',
    label: 'Noir',
    description: 'Cool dark. Steely greys, icy accent.',
    surface: 'dark',
    vars: {
      '--color-ink': '#0a0c10',
      '--color-ink-2': '#11141a',
      '--color-ink-3': '#181c25',
      '--color-ink-4': '#222633',
      '--color-ink-rim': '#2a2f3a',
      '--color-cream': '#f1f3f8',
      '--color-cream-2': '#c4c9d3',
      '--color-cream-3': '#7e8593',
      '--color-cream-4': '#4d5260',
      '--color-clove': '#7ec3ff',
      '--color-clove-2': '#a4d7ff',
      '--color-clove-3': '#162539',
    },
  },
  {
    id: 'forest',
    label: 'Forest',
    description: 'Earthy dark green base, sage accent.',
    surface: 'dark',
    vars: {
      '--color-ink': '#0a1410',
      '--color-ink-2': '#101c18',
      '--color-ink-3': '#162621',
      '--color-ink-4': '#1f332c',
      '--color-ink-rim': '#264036',
      '--color-cream': '#f2f4ee',
      '--color-cream-2': '#c8cdc1',
      '--color-cream-3': '#7c8576',
      '--color-cream-4': '#4f5950',
      '--color-clove': '#6dd58c',
      '--color-clove-2': '#9beea9',
      '--color-clove-3': '#163320',
    },
  },
  {
    id: 'plum',
    label: 'Plum',
    description: 'Deep wine purples, electric pink accent.',
    surface: 'dark',
    vars: {
      '--color-ink': '#0e0814',
      '--color-ink-2': '#170f20',
      '--color-ink-3': '#22162d',
      '--color-ink-4': '#321e42',
      '--color-ink-rim': '#3c244e',
      '--color-cream': '#f5edf6',
      '--color-cream-2': '#d2c1d6',
      '--color-cream-3': '#8a7d96',
      '--color-cream-4': '#534b5e',
      '--color-clove': '#ec5fd5',
      '--color-clove-2': '#f59be7',
      '--color-clove-3': '#3a1233',
    },
  },
  {
    id: 'paper',
    label: 'Paper',
    description: 'Light theme. Crisp paper feel.',
    surface: 'light',
    vars: {
      '--color-ink': '#f5f3ed',
      '--color-ink-2': '#ffffff',
      '--color-ink-3': '#fafaf3',
      '--color-ink-4': '#ebe7da',
      '--color-ink-rim': '#d8d4c5',
      '--color-cream': '#1c1a16',
      '--color-cream-2': '#42403a',
      '--color-cream-3': '#7a766c',
      '--color-cream-4': '#a8a395',
      '--color-clove': '#c2553b',
      '--color-clove-2': '#e88a72',
      '--color-clove-3': '#fbe2da',
    },
  },
];

export interface ResolveInput {
  boardTheme?: string | null;
  boardAccentHue?: number | null;
  userPreference?: string | null;
  /** Operating-system colour scheme — used when userPreference === 'system'. */
  systemSurface?: 'light' | 'dark';
}

export interface ResolvedTheme {
  preset: ThemePreset;
  vars: Record<string, string>;
  surface: 'light' | 'dark';
}

/** Preset ids that the system mapping resolves to. */
const SYSTEM_DARK = 'ember';
const SYSTEM_LIGHT = 'paper';

/**
 * Pure: combine board + user inputs and produce the exact CSS variable
 * set to apply. Tested independently.
 */
export function resolveTheme(input: ResolveInput): ResolvedTheme {
  let id: string | null | undefined;
  if (input.userPreference === 'system') {
    id = input.systemSurface === 'light' ? SYSTEM_LIGHT : SYSTEM_DARK;
  } else if (input.userPreference && input.userPreference !== 'follow_board') {
    id = input.userPreference;
  } else {
    id = input.boardTheme;
  }
  const preset = THEMES.find((t) => t.id === id) ?? THEMES[0]!;
  const vars: Record<string, string> = { ...preset.vars };
  // Accent override: only honour when the user hasn't overridden the
  // theme (or when they explicitly chose 'follow_board'). Otherwise
  // the user's pick should look uniform across boards.
  const honourAccent =
    !input.userPreference || input.userPreference === 'follow_board';
  if (honourAccent && input.boardAccentHue != null) {
    Object.assign(vars, accentFromHue(input.boardAccentHue, preset.surface));
  }
  return { preset, vars, surface: preset.surface };
}

/** Read prefers-color-scheme. SSR-safe. */
export function detectSystemSurface(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Generate accent variable set from a single hue. Light surfaces want
 * darker, more saturated accents (they sit on near-white); dark
 * surfaces want lighter, neon-leaning accents.
 */
export function accentFromHue(
  hue: number,
  surface: 'light' | 'dark',
): Record<string, string> {
  const h = ((hue % 360) + 360) % 360;
  if (surface === 'light') {
    return {
      '--color-clove': `hsl(${h} 70% 42%)`,
      '--color-clove-2': `hsl(${h} 75% 56%)`,
      '--color-clove-3': `hsl(${h} 75% 92%)`,
    };
  }
  return {
    '--color-clove': `hsl(${h} 85% 64%)`,
    '--color-clove-2': `hsl(${h} 88% 78%)`,
    '--color-clove-3': `hsl(${h} 60% 18%)`,
  };
}

export function applyTheme(input: ResolveInput): void {
  if (typeof document === 'undefined') return;
  const merged: ResolveInput = {
    ...input,
    systemSurface: input.systemSurface ?? detectSystemSurface(),
  };
  const resolved = resolveTheme(merged);
  const root = document.documentElement;
  for (const [k, v] of Object.entries(resolved.vars)) {
    root.style.setProperty(k, v);
  }
  root.style.colorScheme = resolved.surface;
  root.dataset.theme = resolved.preset.id;
}

/**
 * Wire up an OS-theme listener so picking 'system' as your preference
 * is live: flipping macOS dark→light updates instantly without reload.
 * Returns a teardown function; safe to call multiple times.
 */
let systemListenerCleanup: (() => void) | null = null;
export function startSystemThemeWatcher(reapply: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  systemListenerCleanup?.();
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  const handler = () => reapply();
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', handler);
    systemListenerCleanup = () => mq.removeEventListener('change', handler);
  } else {
    // Old Safari fallback.
    mq.addListener(handler);
    systemListenerCleanup = () => mq.removeListener(handler);
  }
  return systemListenerCleanup;
}
